import { Banner, Link } from '@customerio/pluma-components/react';
import { formatRelativeTime } from '@customerio/pluma-components/react/utils/formatted-relative-time';
import { useStore } from '@nanostores/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { BILLING_PATHS } from '@/features/billing';
import { accountStore } from '@/services/auth/store';
import { AccountState } from '@/services/auth/types';
import { billing } from '@/services/billing';
import { PlanType } from '@/services/billing/plans';

/** When the account cancels, in words. Without a date, it cancels when the period ends. */
function cancellationTime(cancelsAt: number | undefined): string {
  if (cancelsAt == null) {
    return 'at the end of this billing period';
  }
  return formatRelativeTime(new Date(cancelsAt * 1000), new Date(), {
    absoluteFormatDiffCutoff: null,
    shouldUppercaseFirst: false,
  });
}

/** The billing team reactivates a Premium or Enterprise plan. Other plans reactivate themselves. */
function canSelfServeReactivation(planType: PlanType | undefined): boolean {
  return planType !== PlanType.Premium && planType !== PlanType.Enterprise;
}

function PendingCancellationContent() {
  const cancelsAt = useStore(billing.cancelsAt);
  const planType = useStore(billing.planType);
  const { hasPermission } = usePermissions();
  const canReactivate = hasPermission('account/billing/edit') && canSelfServeReactivation(planType);

  return (
    <Banner
      type="announcement"
      variant="critical"
      title={`Your account will be cancelled ${cancellationTime(cancelsAt)}.`}
      description={
        canReactivate ? (
          <>
            <Link href={`${BILLING_PATHS.overview}?reactivate=true`} variant="secondary">
              Click here
            </Link>{' '}
            to get more time.
          </>
        ) : (
          'Contact an admin to get more time.'
        )
      }
      data-test-pending-cancellation-banner
    />
  );
}

/**
 * Tells an account that is pending cancellation when it cancels, and how to get more time. It waits
 * for billing, because the date and the plan come from there.
 */
export function PendingCancellationBanner() {
  const account = useStore(accountStore);
  const isBillingInitialized = useStore(billing.initialized);
  const isPendingCancellation = account?.account_state === AccountState.PendingCancellation;

  if (!isBillingInitialized || !isPendingCancellation) {
    return null;
  }
  return <PendingCancellationContent />;
}
